// The world painter's tiles on the client (server: simulation/src/worldwire.py has the format, worldchart.py the geometry; design: claude/world-painter-design.md).
//
// The world painter is the cloud artist whose extent is the planet. Its tiles are the global tier (the whole sphere on six cube faces, 27 to 39 km cells) and, under a viewer high enough to
// want them, one nadir window per tier (64 x 64 columns of 16, 8, 4 and 2 km, each on the tangent plane under the window's centre). This file is the client's side of the wire and the
// store the cloud pass (worldCloud.ts) and the planet's ocean (farShell.ts) read:
//
//   * `isWorldFrame` tells a "WCP" frame from a physics frame and from the cloud volume's "CVP" frames; `WorldTileAssembler` puts the frames of a package together (the streams are the
//     package id's low three bits: 0 the global tier, 1 to 4 the windows coarse to fine, each keeps its newest package only), inflates the parts as they arrive and undoes the row filter.
//   * `WorldTiles` keeps the newest tile of each stream as textures. Channels go four to a texel by deck, so that one fetch per source gives a deck everything it needs:
//         group 0  low deck    cover, water, base, top
//         group 1  mid deck    cover, water, base, thickness
//         group 2  high deck   cover, water, base, thickness
//         group 3  low deck    roughness, rain
//     The bytes stay quantised in the textures (the shader undoes the range and the curve of each channel from `ranges`, which come from the tile's own meta, so nothing here is a copy of
//     the server's table).
//   * The global tier is one texture array of 24 layers (face * 4 + group), every layer padded by one cell on each side with the neighbouring face's cells, so that bilinear filtering and
//     the mip chain run across the cube's edges without a seam. The windows are one array of 16 layers (tier * 4 + group), clamped at their edges (the shader fades each into the next coarser
//     tier over its outer tenth).
//
// Not here: where the pictures go on the screen (worldCloud.ts), and when the near field gives way to them (handover.ts).
import * as THREE from 'three';
import { CLOUD_RESP_GLSL, cloudOpticsUniforms } from './cloudOptics';

// ── the wire ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
/** Bytes common to the header of both frame types: "WCP", version, type, 0, 0, 0xFF, then the u32 package id. */
const HEAD = 12;
const KIND_BEGIN = 1, KIND_PART = 2;
const STREAMS = 8;

/** True for one of this module's frames. A physics frame starts with its tick as a u64 (the header's last byte, 0xFF, is never there); the cloud volume's frames start "CVP". */
export function isWorldFrame(buf: ArrayBuffer): boolean {
  if (buf.byteLength < HEAD) return false;
  const b = new Uint8Array(buf, 0, 8);
  return b[0] === 0x57 && b[1] === 0x43 && b[2] === 0x50 && b[3] === 1 && b[7] === 0xFF;
}

async function inflateZ(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Undo the row filter in place: each row (`row` bytes) was stored as every cell's difference from the cell to its left, modulo 256; the first cell as it is. A running sum along the row. */
export function unfilterRows(a: Uint8Array, row: number): void {
  for (let r = 0; r < a.length; r += row) for (let i = r + 1; i < r + row; i++) a[i] = (a[i]! + a[i - 1]!) & 255;
}

export interface WorldChannel { n: string; lo: number; hi: number; g: number }
export interface WorldTileMeta {
  type: 'world_tile';
  v: number;
  kind: 'global' | 'win';
  level: number;
  key: [number, number, number];
  /** Model time the tile was painted for (s). */
  t: number;
  /** Cells across a face (global) or columns across a window. */
  n: number;
  faces: number;
  /** Cell size (m); null for a global tier whose cells vary over the face. */
  dx: number | null;
  /** Planet radius (m), the patch's latitude in the body frame (rad), the patch's half width (m). */
  R: number;
  lat0: number;
  ph: number;
  ch: WorldChannel[];
  /** A window's centre, east and north unit vectors in the body frame. */
  c?: number[]; e?: number[]; nn?: number[];
  /** The global tier's face axes: 6 x [normal, u, v] in the body frame. */
  fa?: number[][][];
  bin: { len: { d: number }; parts: { k: string; o: number; n: number; st: number }[] };
}
/** A whole tile: the meta and the planes in channel order, each (faces, n, n) bytes. */
export interface WorldTile { meta: WorldTileMeta; bytes: Uint8Array }

interface Assembly { id: number; meta: WorldTileMeta; data: Uint8Array; left: number }

/** Collects the frames of the newest package of each stream. A begin frame starts a package and drops an unfinished one of the same stream; parts of any other package are ignored. When the
 *  last part of a package is in, `onTile` gets the tile. */
export class WorldTileAssembler {
  private cur: (Assembly | null)[] = new Array(STREAMS).fill(null);
  /** packages abandoned unfinished (a newer one of the same stream began first), for the console */
  dropped = 0;

  constructor(private readonly onTile: (t: WorldTile) => void, private readonly onError?: (e: unknown) => void) {}

  accept(buf: ArrayBuffer): void {
    const v = new DataView(buf);
    const kind = v.getUint8(4), id = v.getUint32(8, true), s = id & (STREAMS - 1);
    if (kind === KIND_BEGIN) {
      let meta: WorldTileMeta;
      try {
        meta = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 16, v.getUint32(12, true)))) as WorldTileMeta;
        if (meta.type !== 'world_tile' || !meta.bin || !Array.isArray(meta.bin.parts)) throw new Error('not a world tile');
      } catch (e) { this.cur[s] = null; this.onError?.(e); return; }
      const old = this.cur[s];
      if (old && old.left > 0) this.dropped++;
      this.cur[s] = { id, meta, data: new Uint8Array(meta.bin.len.d), left: meta.bin.parts.length };
      return;
    }
    const a = this.cur[s];
    if (kind !== KIND_PART || !a || a.id !== id) return;
    const spec = a.meta.bin.parts[v.getUint16(12, true)];
    if (!spec) return;
    inflateZ(new Uint8Array(buf, 14)).then(raw => {
      if (this.cur[s] !== a) return;                                       // a newer package of this stream began while this one was inflating
      if (raw.length !== spec.n || spec.o + spec.n > a.data.length) throw new Error(`world tile part @${spec.o}: ${raw.length} bytes, expected ${spec.n}`);
      if (spec.st) unfilterRows(raw, spec.st);
      a.data.set(raw, spec.o);
      if (--a.left === 0) { this.cur[s] = null; this.onTile({ meta: a.meta, bytes: a.data }); }
    }).catch(e => { if (this.cur[s] === a) this.cur[s] = null; this.onError?.(e); });
  }
}

// ── the store ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
/** The channels of each texture group, in texel order ('' for an empty slot). */
export const TILE_GROUPS: readonly (readonly string[])[] = [
  ['cov_l', 'wat_l', 'base_l', 'top_l'],
  ['cov_m', 'wat_m', 'base_m', 'thick_m'],
  ['cov_h', 'wat_h', 'base_h', 'thick_h'],
  ['rough_l', 'rain', '', ''],
];
/** The nadir tiers, coarse to fine: the level numbers the server gives them (worldchart.TIERS) and the columns across a window. */
export const WIN_LEVEL0 = 9;
export const WIN_TIERS = 4;
export const WIN_N = 64;

export class WorldTiles {
  /** The global tier: 24 layers (face * 4 + group) of (n + 2)^2 texels, RGBA8, mipmapped. */
  readonly global: THREE.DataArrayTexture;
  /** The windows: 16 layers (tier * 4 + group) of 64^2 texels, RGBA8. */
  readonly win: THREE.DataArrayTexture;
  /** Per channel slot (group * 4 + k): lo, hi - lo, 1 / g, 0: the shader's dequantisation (value = lo + span * (byte / 255) ^ (1 / g)). */
  readonly ranges: THREE.Vector4[] = [];
  /** The cube faces' axes (normal, u, v per face) in the body frame. */
  readonly faceAxes: THREE.Vector3[] = [];
  /** Per window tier: centre (xyz) and cell size (w); east and north unit vectors; 1 when the tier holds a window. All in the body frame. */
  readonly winC: THREE.Vector4[] = [];
  readonly winE: THREE.Vector3[] = [];
  readonly winN: THREE.Vector3[] = [];
  readonly winOn: number[] = [];
  /** The model time each stream's tile was painted for (global first). */
  readonly stamp: number[] = [NaN, NaN, NaN, NaN, NaN];
  /** Whether a global tier has arrived (the cloud pass draws nothing before). */
  haveGlobal = false;
  /** Planet radius (m), the patch's latitude (rad, body frame) and half width (m), the global tier's cells across a face and their nominal size (m). */
  R = 6371000; lat0 = 0; patchHalf = 256e3; nGlobal = 256; dxGlobal = 39e3;
  /** Incremented by every tile that arrives. */
  version = 0;

  private gData = new Uint8Array(24 * 258 * 258 * 4);
  private wData = new Uint8Array(WIN_TIERS * 4 * WIN_N * WIN_N * 4);
  private border: { dst: Int32Array; src: Int32Array; n: number } | null = null;

  constructor() {
    const P = 258;
    this.global = new THREE.DataArrayTexture(this.gData, P, P, 24);
    this.win = new THREE.DataArrayTexture(this.wData, WIN_N, WIN_N, WIN_TIERS * 4);
    for (const t of [this.global, this.win]) {
      t.format = THREE.RGBAFormat; t.type = THREE.UnsignedByteType; t.colorSpace = THREE.NoColorSpace;
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.magFilter = THREE.LinearFilter; t.flipY = false; t.unpackAlignment = 1;
    }
    this.global.generateMipmaps = true; this.global.minFilter = THREE.LinearMipmapLinearFilter;
    this.win.generateMipmaps = false; this.win.minFilter = THREE.LinearFilter;
    for (let i = 0; i < 16; i++) this.ranges.push(new THREE.Vector4(0, 1, 1, 0));
    for (let i = 0; i < 18; i++) this.faceAxes.push(new THREE.Vector3());
    for (let i = 0; i < WIN_TIERS; i++) { this.winC.push(new THREE.Vector4(0, 0, 1, 1)); this.winE.push(new THREE.Vector3(1, 0, 0)); this.winN.push(new THREE.Vector3(0, 1, 0)); this.winOn.push(0); }
  }

  /** Forget everything (a new world, or no server). */
  reset(): void {
    this.haveGlobal = false;
    this.winOn.fill(0);
    this.stamp.fill(NaN);
    this.version++;
  }

  /** A tile arrived whole (WorldTileAssembler). */
  ingest(tile: WorldTile): void {
    const m = tile.meta;
    this.R = m.R; this.lat0 = m.lat0; this.patchHalf = m.ph;
    this.setRanges(m.ch);
    if (m.kind === 'global') this.ingestGlobal(tile); else this.ingestWindow(tile);
    this.version++;
  }

  private setRanges(ch: readonly WorldChannel[]): void {
    for (let g = 0; g < TILE_GROUPS.length; g++) {
      for (let k = 0; k < 4; k++) {
        const nm = TILE_GROUPS[g]![k]!;
        const c = nm ? ch.find(x => x.n === nm) : undefined;
        if (c) this.ranges[g * 4 + k]!.set(c.lo, c.hi - c.lo, 1 / c.g, 0);
        else this.ranges[g * 4 + k]!.set(0, 1, 1, 0);
      }
    }
  }

  /** Where each channel sits in the tile's planes: [group][slot] -> plane index, or -1. */
  private planeIndex(ch: readonly WorldChannel[]): number[][] {
    return TILE_GROUPS.map(row => row.map(nm => (nm ? ch.findIndex(x => x.n === nm) : -1)));
  }

  private ingestGlobal(tile: WorldTile): void {
    const m = tile.meta, n = m.n, P = n + 2;
    if (m.fa) for (let f = 0; f < 6; f++) for (let a = 0; a < 3; a++) this.faceAxes[f * 3 + a]!.set(m.fa[f]![a]![0]!, m.fa[f]![a]![1]!, m.fa[f]![a]![2]!);
    if (n !== this.nGlobal || !this.border || this.border.dst.length === 0) { this.nGlobal = n; this.rebuildGlobal(n); }
    this.dxGlobal = m.dx ?? (Math.PI * 0.5 * m.R / n);
    const idx = this.planeIndex(m.ch), plane = n * n * m.faces;
    const d = this.gData;
    for (let f = 0; f < 6; f++) {
      for (let g = 0; g < 4; g++) {
        const base = (f * 4 + g) * P * P * 4;
        for (let k = 0; k < 4; k++) {
          const pi = idx[g]![k]!;
          if (pi < 0) continue;
          const src = pi * plane + f * n * n;
          for (let j = 0; j < n; j++) {
            let o = base + ((j + 1) * P + 1) * 4 + k, s = src + j * n;
            for (let i = 0; i < n; i++, o += 4, s++) d[o] = tile.bytes[s]!;
          }
        }
      }
    }
    // the border: each padded texel takes the nearest cell of whichever face its direction falls on
    const b = this.border!;
    for (let q = 0; q < b.dst.length; q++) {
      const dst = b.dst[q]!, srcCell = b.src[q]!;                           // dst: (face * P * P + texel) ; src: (face * n * n + cell)
      const df = Math.floor(dst / (P * P)), dt = dst - df * P * P;
      const sf = Math.floor(srcCell / (n * n)), sc = srcCell - sf * n * n;
      const sj = Math.floor(sc / n), si = sc - sj * n;
      for (let g = 0; g < 4; g++) {
        const dbase = ((df * 4 + g) * P * P + dt) * 4, sbase = ((sf * 4 + g) * P * P + (sj + 1) * P + (si + 1)) * 4;
        for (let k = 0; k < 4; k++) d[dbase + k] = d[sbase + k]!;
      }
    }
    this.global.needsUpdate = true;
    this.stamp[0] = m.t;
    this.haveGlobal = true;
  }

  /** The padded texels of the global tier and the cell each takes its value from (the neighbouring face's, by direction). */
  private rebuildGlobal(n: number): void {
    const P = n + 2;
    const dst: number[] = [], src: number[] = [];
    const fa = this.faceAxes;
    const ready = fa.every(v => v.lengthSq() > 0.5);
    if (!ready) { this.border = { dst: new Int32Array(0), src: new Int32Array(0), n }; return; }
    for (let f = 0; f < 6; f++) {
      const N = fa[f * 3]!, U = fa[f * 3 + 1]!, V = fa[f * 3 + 2]!;
      for (let pj = 0; pj < P; pj++) {
        for (let pi = 0; pi < P; pi++) {
          if (pi > 0 && pi < P - 1 && pj > 0 && pj < P - 1) continue;
          const a = Math.tan((2 * (pi - 1 + 0.5) / n - 1) * Math.PI / 4), c = Math.tan((2 * (pj - 1 + 0.5) / n - 1) * Math.PI / 4);
          const x = N.x + a * U.x + c * V.x, y = N.y + a * U.y + c * V.y, z = N.z + a * U.z + c * V.z;
          const ax = Math.abs(x), ay = Math.abs(y), az = Math.abs(z);
          const k = ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
          const comp = k === 0 ? x : k === 1 ? y : z;
          const face = 2 * k + (comp > 0 ? 0 : 1);
          const N2 = fa[face * 3]!, U2 = fa[face * 3 + 1]!, V2 = fa[face * 3 + 2]!;
          const w = x * N2.x + y * N2.y + z * N2.z;
          const aa = Math.atan((x * U2.x + y * U2.y + z * U2.z) / w) * (4 / Math.PI), bb = Math.atan((x * V2.x + y * V2.y + z * V2.z) / w) * (4 / Math.PI);
          const ci = Math.min(n - 1, Math.max(0, Math.floor((aa + 1) * 0.5 * n))), cj = Math.min(n - 1, Math.max(0, Math.floor((bb + 1) * 0.5 * n)));
          dst.push(f * P * P + pj * P + pi);
          src.push(face * n * n + cj * n + ci);
        }
      }
    }
    this.border = { dst: Int32Array.from(dst), src: Int32Array.from(src), n };
  }

  private ingestWindow(tile: WorldTile): void {
    const m = tile.meta, ti = m.level - WIN_LEVEL0;
    if (ti < 0 || ti >= WIN_TIERS || m.n !== WIN_N || !m.c || !m.e || !m.nn) return;
    const idx = this.planeIndex(m.ch), n = WIN_N, plane = n * n;
    const d = this.wData;
    for (let g = 0; g < 4; g++) {
      const base = (ti * 4 + g) * n * n * 4;
      for (let k = 0; k < 4; k++) {
        const pi = idx[g]![k]!;
        if (pi < 0) continue;
        for (let s = 0, o = base + k; s < plane; s++, o += 4) d[o] = tile.bytes[pi * plane + s]!;
      }
    }
    this.winC[ti]!.set(m.c[0]!, m.c[1]!, m.c[2]!, m.dx ?? 2000);
    this.winE[ti]!.set(m.e[0]!, m.e[1]!, m.e[2]!);
    this.winN[ti]!.set(m.nn[0]!, m.nn[1]!, m.nn[2]!);
    this.winOn[ti] = 1;
    this.stamp[1 + ti] = m.t;
    this.win.needsUpdate = true;
  }

  /** The per-frame uniforms of TILES_GLSL on a material made with `tileUniforms()`. */
  setUniforms(u: Record<string, THREE.IUniform>): void {
    (u.uLat!.value as THREE.Vector2).set(Math.sin(this.lat0), Math.cos(this.lat0));
    (u.uTP!.value as THREE.Vector4).set(this.R, this.nGlobal, this.dxGlobal, 0);
  }

  dispose(): void { this.global.dispose(); this.win.dispose(); }
}

// ── the tiles in a shader ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * Reading the tiles: the including shader defines `PI_` first. `fieldAt(b, deck, lod, lnDx)` is a deck's four channels (low: cover, water, base, top; mid and high: cover, water, base,
 * thickness) at the body-frame direction `b`: the global tier under the windows. `toBody(q)` takes a direction's coordinates in the patch's own frame (east, north, up: the registry's
 * body-fixed frame for the home planet) to the body frame the tiles are in. `deckResp` (cloudOptics.ts) is what the cloud itself does to light, from its cell's cover and water, read from the world's cloud tables.
 */
export const TILES_GLSL = /* glsl */ `
precision highp sampler2DArray;
uniform sampler2DArray uTG;      // the global tier: layer = face * 4 + group, (n + 2)^2 texels
uniform sampler2DArray uTW;      // the windows: layer = tier * 4 + group, ${WIN_N}^2 texels
uniform vec4  uTR[16];           // per channel slot (group * 4 + k): lo, span, 1 / curve, 0
uniform vec3  uFA[18];           // the cube faces' axes (normal, u, v) in the body frame
uniform vec4  uWc[${WIN_TIERS}]; // a window's centre (body frame) and cell size
uniform vec3  uWe[${WIN_TIERS}];
uniform vec3  uWn[${WIN_TIERS}];
uniform float uWon[${WIN_TIERS}];
uniform vec2  uLat;              // sin, cos of the patch's latitude in the body frame
uniform vec4  uTP;               // planet radius, cells across a global face, a global cell (m), 0

vec3 toBody(vec3 q) { return vec3(-uLat.x * q.y + uLat.y * q.z, q.x, uLat.y * q.y + uLat.x * q.z); }

vec4 dq(vec4 t, int g) {
  vec4 a = uTR[g * 4], b = uTR[g * 4 + 1], c = uTR[g * 4 + 2], d = uTR[g * 4 + 3];
  return vec4(a.x + a.y * pow(t.x, a.z), b.x + b.y * pow(t.y, b.z), c.x + c.y * pow(t.z, c.z), d.x + d.y * pow(t.w, d.z));
}

// the cube face of a direction and its equiangular coordinates in the face (-1 .. 1 across it): worldchart.cell_of
int faceOf(vec3 b, out vec2 ab) {
  vec3 A = abs(b);
  int f = (A.x >= A.y && A.x >= A.z) ? (b.x > 0.0 ? 0 : 1) : (A.y >= A.z ? (b.y > 0.0 ? 2 : 3) : (b.z > 0.0 ? 4 : 5));
  float w = dot(b, uFA[f * 3]);
  ab = vec2(atan(dot(b, uFA[f * 3 + 1]) / w), atan(dot(b, uFA[f * 3 + 2]) / w)) * (4.0 / PI_);
  return f;
}

// A deck's four channels at body direction b, the global tier under the windows (each fading into the next coarser over its outer tenth); lnDx the log of the cell size read.
vec4 fieldAt(vec3 b, int k, float lod, out float lnDx) {
  vec2 ab;
  int f = faceOf(b, ab);
  float n = uTP.y;
  vec2 xy = (ab * 0.5 + 0.5) * n + 1.0;
  vec4 v = dq(textureLod(uTG, vec3(xy / (n + 2.0), float(f * 4 + k)), lod), k);
  lnDx = log(uTP.z);
  for (int t = 0; t < ${WIN_TIERS}; t++) {
    if (uWon[t] < 0.5) continue;
    float cs = dot(b, uWc[t].xyz);
    if (cs <= 0.0) continue;
    float ex = dot(b, uWe[t]), ny = dot(b, uWn[t]);
    float s = length(vec2(ex, ny));
    float kk = s > 1e-9 ? atan(s, cs) / s : 1.0;
    float dx = uWc[t].w;
    vec2 uv = uTP.x * kk * vec2(ex, ny) / (float(${WIN_N}) * dx) + 0.5;
    float e = min(min(uv.x, 1.0 - uv.x), min(uv.y, 1.0 - uv.y));
    float wt = smoothstep(0.0, 0.1, e);
    if (wt <= 0.0) continue;
    v = mix(v, dq(textureLod(uTW, vec3(uv, float(t * 4 + k)), 0.0), k), wt);
    lnDx = mix(lnDx, log(dx), wt);
  }
  return v;
}

${CLOUD_RESP_GLSL}
`;

/** The uniforms of TILES_GLSL, by reference to the store's own arrays and textures (a material that takes them stays current; `WorldTiles.setUniforms` does the few that are per frame). */
export function tileUniforms(): Record<string, THREE.IUniform> {
  const T = worldTiles;
  return {
    uTG: { value: T.global }, uTW: { value: T.win }, uTR: { value: T.ranges }, uFA: { value: T.faceAxes },
    uWc: { value: T.winC }, uWe: { value: T.winE }, uWn: { value: T.winN }, uWon: { value: T.winOn },
    uLat: { value: new THREE.Vector2(0, 1) }, uTP: { value: new THREE.Vector4(6371000, 256, 39000, 0) },
    ...cloudOpticsUniforms(),
  };
}

/** The one store: the broadcast connection feeds it, the cloud pass and the ocean read it (as planet.ts is for the planet's radius). */
export const worldTiles = new WorldTiles();
